import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Loader2, Plus, Trash2, Save, RotateCcw, ChevronDown, GripVertical, Users, Building2, Link2, Unlink, Search, Check } from 'lucide-react';
import { useKB } from './KBContext';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const CATEGORIES = [
  { value: 'board', label: 'Board' },
  { value: 'committee', label: 'Committee' },
  { value: 'staff', label: 'Staff' },
  { value: 'chapter', label: 'Chapter' },
];

const inp = {
  padding: '8px 10px', borderRadius: 7, border: '1px solid var(--kb-border)',
  background: 'var(--kb-bg-soft, rgba(255,255,255,0.03))', color: 'var(--kb-fg)',
  fontSize: 13, outline: 'none', width: '100%', boxSizing: 'border-box',
};

const blankGroup = () => ({
  slug: null, name: '', short_name: '', category: 'committee',
  description: '', order: 100, is_active: true, members: [], _new: true, _open: true,
});

// Inline typeahead to link a governance person to a real member profile.
// Sets `member_id` (explicit link, always wins server-side). Falls back to
// automatic name-matching when no explicit link is set.
const MemberLinkPicker = ({ member, password, onLink, onUnlink }) => {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState('');
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const wrapRef = useRef(null);
  const linked = !!member.member_id;
  const linkedName = member._linked_name || '';

  useEffect(() => {
    const onDoc = (e) => { if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, []);

  useEffect(() => {
    if (!open || q.trim().length < 2) { setResults([]); return; }
    let cancelled = false;
    setLoading(true);
    const t = setTimeout(async () => {
      try {
        const res = await fetch(`${API_URL}/api/kb/admin/governance/member-search`, {
          method: 'POST', headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ password, q: q.trim() }),
        });
        const d = res.ok ? await res.json() : { items: [] };
        if (!cancelled) setResults(d.items || []);
      } catch (e) { if (!cancelled) setResults([]); }
      if (!cancelled) setLoading(false);
    }, 250);
    return () => { cancelled = true; clearTimeout(t); };
  }, [q, open, password]);

  return (
    <div ref={wrapRef} style={{ position: 'relative' }}>
      <button type="button"
        data-testid="kb-gov-member-link-btn"
        onClick={() => { setOpen((v) => !v); setQ(member.name || ''); }}
        title={linked ? `Linked${linkedName ? ' to ' + linkedName : ''}` : 'Link to a member profile'}
        style={{
          display: 'flex', alignItems: 'center', justifyContent: 'center', width: 30, height: 30,
          borderRadius: 7, cursor: 'pointer',
          border: `1px solid ${linked ? 'rgba(37,99,235,0.45)' : 'var(--kb-border)'}`,
          background: linked ? 'rgba(37,99,235,0.12)' : 'transparent',
          color: linked ? 'var(--kb-accent, #2563EB)' : 'var(--kb-fg-soft, #8B96A5)',
        }}>
        <Link2 size={14} />
      </button>

      {open && (
        <div data-testid="kb-gov-member-link-panel" style={{
          position: 'absolute', top: 34, right: 0, zIndex: 30, width: 320,
          background: 'var(--kb-bg, #15171c)', border: '1px solid var(--kb-border)',
          borderRadius: 10, boxShadow: '0 12px 32px rgba(0,0,0,0.35)', padding: 10,
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 8 }}>
            <Search size={14} style={{ color: 'var(--kb-fg-soft, #8B96A5)', flexShrink: 0 }} />
            <input autoFocus value={q} onChange={(e) => setQ(e.target.value)}
              data-testid="kb-gov-member-link-input"
              placeholder="Search member by name or company…"
              style={{ ...inp, padding: '6px 8px' }} />
          </div>
          {linked && (
            <button type="button" onClick={() => { onUnlink(); setOpen(false); }}
              data-testid="kb-gov-member-unlink"
              style={{ display: 'flex', alignItems: 'center', gap: 6, width: '100%', padding: '7px 8px',
                marginBottom: 6, borderRadius: 7, border: '1px solid rgba(239,68,68,0.4)',
                background: 'transparent', color: '#EF4444', fontSize: 12.5, cursor: 'pointer' }}>
              <Unlink size={13} /> Remove link{linkedName ? ` (${linkedName})` : ''}
            </button>
          )}
          <div style={{ maxHeight: 240, overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: 4 }}>
            {loading && <div style={{ padding: 8, fontSize: 12, color: 'var(--kb-fg-soft, #8B96A5)' }}><Loader2 size={13} className="spin" /> Searching…</div>}
            {!loading && q.trim().length >= 2 && results.length === 0 && (
              <div style={{ padding: 8, fontSize: 12, color: 'var(--kb-fg-soft, #8B96A5)' }}>No matching members.</div>
            )}
            {results.map((r) => {
              const isCur = r.member_id === member.member_id;
              return (
                <button key={r.member_id} type="button"
                  data-testid="kb-gov-member-link-result"
                  onClick={() => { onLink(r); setOpen(false); }}
                  style={{ display: 'flex', alignItems: 'center', gap: 8, width: '100%', textAlign: 'left',
                    padding: '7px 8px', borderRadius: 7, border: '1px solid var(--kb-border)',
                    background: isCur ? 'rgba(37,99,235,0.12)' : 'transparent', color: 'var(--kb-fg)', cursor: 'pointer' }}>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontSize: 13, fontWeight: 600, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{r.full_name}</div>
                    {r.company_name && <div style={{ fontSize: 11.5, color: 'var(--kb-fg-soft, #8B96A5)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{r.company_name}</div>}
                  </div>
                  {isCur && <Check size={14} style={{ color: 'var(--kb-accent, #2563EB)', flexShrink: 0 }} />}
                </button>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
};

const GroupEditor = ({ group, onChange, onSave, onDelete, saving, password }) => {
  const update = (patch) => onChange({ ...group, ...patch });
  const updateMember = (i, patch) => {
    const members = group.members.map((m, idx) => (idx === i ? { ...m, ...patch } : m));
    update({ members });
  };
  const addMember = () => update({ members: [...group.members, { name: '', company: '', role: '', member_id: null }] });
  const removeMember = (i) => update({ members: group.members.filter((_, idx) => idx !== i) });

  return (
    <div data-testid="kb-gov-group-editor" style={{
      border: '1px solid var(--kb-border)', borderRadius: 12, marginBottom: 14, overflow: 'hidden',
      background: 'var(--kb-bg, rgba(255,255,255,0.02))',
    }}>
      <button type="button" onClick={() => update({ _open: !group._open })}
        data-testid="kb-gov-group-toggle"
        style={{ display: 'flex', alignItems: 'center', gap: 10, width: '100%', textAlign: 'left',
          padding: '12px 14px', background: 'none', border: 'none', cursor: 'pointer', color: 'var(--kb-fg)' }}>
        <Users size={15} style={{ color: 'var(--kb-accent, #2563EB)' }} />
        <span style={{ flex: 1, fontWeight: 600, fontSize: 14 }}>
          {group.name || group._new ? (group.name || 'New group') : group.name}
          <span style={{ marginLeft: 8, fontSize: 11, color: 'var(--kb-fg-soft, #8B96A5)', fontWeight: 500 }}>
            {group.category} · {group.members.length} {group.members.length === 1 ? 'person' : 'people'}{group.is_active ? '' : ' · hidden'}
          </span>
          {group.is_customized && (
            <span data-testid="kb-gov-edited-badge" title="You've edited this group — it won't be overwritten by app updates"
              style={{ marginLeft: 8, fontSize: 10, fontWeight: 700, letterSpacing: '0.04em',
                padding: '2px 7px', borderRadius: 999, background: 'rgba(34,197,94,0.15)', color: '#16A34A' }}>
              EDITED
            </span>
          )}
        </span>
        <ChevronDown size={15} style={{ transform: group._open ? 'rotate(180deg)' : '', transition: 'transform 0.2s', color: 'var(--kb-fg-soft, #8B96A5)' }} />
      </button>

      {group._open && (
        <div style={{ padding: '4px 14px 16px' }}>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, marginBottom: 10 }}>
            <label style={lbl}>Name
              <input style={inp} value={group.name} data-testid="kb-gov-name"
                onChange={(e) => update({ name: e.target.value })} placeholder="Audit Committee" />
            </label>
            <label style={lbl}>Short name
              <input style={inp} value={group.short_name}
                onChange={(e) => update({ short_name: e.target.value })} placeholder="Audit" />
            </label>
            <label style={lbl}>Category
              <select style={inp} value={group.category} data-testid="kb-gov-category"
                onChange={(e) => update({ category: e.target.value })}>
                {CATEGORIES.map((c) => <option key={c.value} value={c.value}>{c.label}</option>)}
              </select>
            </label>
            <label style={lbl}>Display order
              <input type="number" style={inp} value={group.order}
                onChange={(e) => update({ order: parseInt(e.target.value, 10) || 0 })} />
            </label>
          </div>
          <label style={{ ...lbl, marginBottom: 10 }}>Description
            <textarea style={{ ...inp, resize: 'vertical', minHeight: 50 }} value={group.description}
              onChange={(e) => update({ description: e.target.value })} rows={2} />
          </label>
          <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, color: 'var(--kb-fg)', marginBottom: 14, cursor: 'pointer' }}>
            <input type="checkbox" checked={group.is_active} onChange={(e) => update({ is_active: e.target.checked })} />
            Visible in the directory
          </label>

          {/* Members */}
          <div style={{ fontSize: 12, fontWeight: 700, letterSpacing: '0.05em', textTransform: 'uppercase', color: 'var(--kb-fg-soft, #8B96A5)', marginBottom: 8 }}>
            People
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginBottom: 10 }}>
            {group.members.map((m, i) => (
              <div key={i} data-testid="kb-gov-member-row" style={{ display: 'grid', gridTemplateColumns: '16px 1.25fr 1.1fr 1fr 1.15fr 30px 28px', gap: 8, alignItems: 'center' }}>
                <GripVertical size={14} style={{ color: 'var(--kb-fg-soft, #8B96A5)' }} />
                <input style={inp} value={m.name} placeholder="Full name" data-testid="kb-gov-member-name"
                  onChange={(e) => updateMember(i, { name: e.target.value })} />
                <input style={inp} value={m.company || ''} placeholder="Company"
                  onChange={(e) => updateMember(i, { company: e.target.value })} />
                <input style={inp} value={m.role || ''} placeholder="Role (e.g. Chair)"
                  onChange={(e) => updateMember(i, { role: e.target.value })} />
                <input style={inp} value={m.email || ''} placeholder="Email (optional)" data-testid="kb-gov-member-email"
                  onChange={(e) => updateMember(i, { email: e.target.value })} />
                <MemberLinkPicker member={m} password={password}
                  onLink={(r) => updateMember(i, { member_id: r.member_id, _linked_name: r.full_name })}
                  onUnlink={() => updateMember(i, { member_id: null, _linked_name: '' })} />
                <button type="button" onClick={() => removeMember(i)} data-testid="kb-gov-member-remove"
                  style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#EF4444', display: 'flex', justifyContent: 'center' }}>
                  <Trash2 size={15} />
                </button>
              </div>
            ))}
          </div>
          <button type="button" onClick={addMember} data-testid="kb-gov-add-member"
            style={{ ...ghostBtn, marginBottom: 14 }}>
            <Plus size={14} /> Add person
          </button>

          <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', borderTop: '1px solid var(--kb-border)', paddingTop: 12 }}>
            {!group._new && (
              <button type="button" onClick={() => onDelete(group)} data-testid="kb-gov-delete"
                style={{ ...ghostBtn, color: '#EF4444', borderColor: 'rgba(239,68,68,0.4)' }}>
                <Trash2 size={14} /> Delete group
              </button>
            )}
            <button type="button" onClick={() => onSave(group)} disabled={saving || !group.name.trim()}
              data-testid="kb-gov-save"
              style={{ display: 'inline-flex', alignItems: 'center', gap: 6, padding: '8px 16px', borderRadius: 8,
                border: 'none', background: 'var(--kb-accent, #2563EB)', color: '#fff', fontWeight: 600, fontSize: 13,
                cursor: 'pointer', opacity: (saving || !group.name.trim()) ? 0.6 : 1 }}>
              {saving ? <Loader2 size={14} className="spin" /> : <Save size={14} />} Save
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

const lbl = { display: 'flex', flexDirection: 'column', gap: 5, fontSize: 12, fontWeight: 600, color: 'var(--kb-fg-soft, #8B96A5)' };
const ghostBtn = {
  display: 'inline-flex', alignItems: 'center', gap: 6, padding: '8px 14px', borderRadius: 8,
  border: '1px solid var(--kb-border)', background: 'transparent', color: 'var(--kb-fg)',
  fontSize: 13, fontWeight: 500, cursor: 'pointer',
};

const ORG_FIELDS = [
  { key: 'name', label: 'Full name', full: true },
  { key: 'short_name', label: 'Short name' },
  { key: 'founded', label: 'Founded' },
  { key: 'tagline', label: 'Tagline', full: true },
  { key: 'website', label: 'Website' },
  { key: 'email', label: 'Email' },
  { key: 'phone', label: 'Phone' },
  { key: 'address', label: 'Address', full: true },
  { key: 'logo_url', label: 'Logo URL', full: true },
];

const OrgProfileEditor = ({ org, password, onSaved, autoOpenSignal, onConsumed }) => {
  const [form, setForm] = useState(org || {});
  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [note, setNote] = useState('');
  const rootRef = React.useRef(null);

  useEffect(() => { setForm(org || {}); }, [org]);

  // When the admin opens "IPMI Profile" from the account menu, expand + scroll here.
  useEffect(() => {
    if (autoOpenSignal) {
      setOpen(true);
      setTimeout(() => {
        rootRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }, 100);
      onConsumed && onConsumed();
    }
  }, [autoOpenSignal, onConsumed]);

  if (!org) return null;

  const save = async () => {
    setSaving(true); setNote('');
    try {
      const payload = { password };
      ORG_FIELDS.forEach((f) => { payload[f.key] = form[f.key] || ''; });
      payload.description = form.description || '';
      const res = await fetch(`${API_URL}/api/kb/admin/org/save`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      if (res.ok) {
        const data = await res.json().catch(() => ({}));
        setNote('Saved.');
        setTimeout(() => setNote(''), 3000);
        onSaved && onSaved(data.org);
      } else { setNote('Save failed.'); }
    } catch (e) { setNote('Connection error.'); }
    setSaving(false);
  };

  return (
    <div ref={rootRef} data-testid="kb-org-profile-editor" style={{
      border: '1px solid var(--kb-border)', borderRadius: 12, marginBottom: 18, overflow: 'hidden',
      background: 'var(--kb-bg, rgba(255,255,255,0.02))',
    }}>
      <button type="button" onClick={() => setOpen(!open)} data-testid="kb-org-editor-toggle"
        style={{ display: 'flex', alignItems: 'center', gap: 10, width: '100%', textAlign: 'left',
          padding: '12px 14px', background: 'none', border: 'none', cursor: 'pointer', color: 'var(--kb-fg)' }}>
        <Building2 size={15} style={{ color: 'var(--kb-accent, #2563EB)' }} />
        <span style={{ flex: 1, fontWeight: 600, fontSize: 14 }}>
          IPMI Profile
          <span style={{ marginLeft: 8, fontSize: 11, color: 'var(--kb-fg-soft, #8B96A5)', fontWeight: 500 }}>
            name · description · contact · logo
          </span>
        </span>
        <ChevronDown size={15} style={{ transform: open ? 'rotate(180deg)' : '', transition: 'transform 0.2s', color: 'var(--kb-fg-soft, #8B96A5)' }} />
      </button>

      {open && (
        <div style={{ padding: '4px 14px 16px' }}>
          <div style={{ display: 'flex', gap: 14, marginBottom: 14, alignItems: 'center' }}>
            {form.logo_url ? (
              <div style={{ width: 64, height: 64, borderRadius: 12, background: '#fff', border: '1px solid var(--kb-border)', padding: 8, flexShrink: 0 }}>
                <img src={form.logo_url} alt="logo" style={{ width: '100%', height: '100%', objectFit: 'contain' }} />
              </div>
            ) : null}
            <span style={{ fontSize: 12, color: 'var(--kb-fg-soft, #8B96A5)' }}>Logo preview</span>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, marginBottom: 10 }}>
            {ORG_FIELDS.map((f) => (
              <label key={f.key} style={{ ...lbl, gridColumn: f.full ? '1 / -1' : 'auto' }}>{f.label}
                <input style={inp} value={form[f.key] || ''} data-testid={`kb-org-field-${f.key}`}
                  onChange={(e) => setForm({ ...form, [f.key]: e.target.value })} />
              </label>
            ))}
          </div>
          <label style={{ ...lbl, marginBottom: 12 }}>Description
            <textarea style={{ ...inp, resize: 'vertical', minHeight: 80 }} value={form.description || ''}
              data-testid="kb-org-field-description"
              onChange={(e) => setForm({ ...form, description: e.target.value })} rows={4} />
          </label>
          <div style={{ display: 'flex', gap: 10, alignItems: 'center', justifyContent: 'flex-end', borderTop: '1px solid var(--kb-border)', paddingTop: 12 }}>
            {note && <span style={{ fontSize: 13, color: 'var(--kb-accent, #2563EB)', marginRight: 'auto' }}>{note}</span>}
            <button type="button" onClick={save} disabled={saving} data-testid="kb-org-save"
              style={{ display: 'inline-flex', alignItems: 'center', gap: 6, padding: '8px 16px', borderRadius: 8,
                border: 'none', background: 'var(--kb-accent, #2563EB)', color: '#fff', fontWeight: 600, fontSize: 13,
                cursor: 'pointer', opacity: saving ? 0.6 : 1 }}>
              {saving ? <Loader2 size={14} className="spin" /> : <Save size={14} />} Save profile
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

const GovernanceAdmin = () => {
  const { password, adminFocus, setAdminFocus } = useKB();
  const [groups, setGroups] = useState([]);
  const [org, setOrg] = useState(null);
  const [loading, setLoading] = useState(true);
  const [savingSlug, setSavingSlug] = useState(null);
  const [msg, setMsg] = useState('');
  const [reseeding, setReseeding] = useState(false);

  const load = useCallback(async (keepOpenSlug = null) => {
    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/kb/admin/governance/list`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ password }),
      });
      if (res.ok) {
        const data = await res.json();
        setGroups((data.groups || []).map((g) => ({ ...g, _open: g.slug === keepOpenSlug })));
        setOrg(data.org || null);
      }
    } catch (e) { console.error(e); }
    setLoading(false);
  }, [password]);

  useEffect(() => { if (password) load(); }, [password, load]);

  const updateGroup = (idx, next) => setGroups((gs) => gs.map((g, i) => (i === idx ? next : g)));

  const saveGroup = async (group) => {
    setSavingSlug(group.slug || 'new'); setMsg('');
    try {
      const res = await fetch(`${API_URL}/api/kb/admin/governance/save`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          password, slug: group.slug, name: group.name, short_name: group.short_name,
          category: group.category, description: group.description, order: group.order,
          is_active: group.is_active,
          members: group.members.filter((m) => (m.name || '').trim()),
        }),
      });
      if (res.ok) {
        const d = await res.json().catch(() => ({}));
        setMsg(`Saved "${group.name}".`);
        await load(d.slug || group.slug);
      } else { const d = await res.json().catch(() => ({})); setMsg(d.detail || 'Save failed.'); }
    } catch (e) { setMsg('Connection error.'); }
    setSavingSlug(null);
  };

  const deleteGroup = async (group) => {
    if (group._new) { setGroups((gs) => gs.filter((g) => g !== group)); return; }
    if (!window.confirm(`Delete "${group.name}"? This cannot be undone.`)) return;
    setMsg('');
    try {
      const res = await fetch(`${API_URL}/api/kb/admin/governance/delete`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ password, slug: group.slug }),
      });
      if (res.ok) { setMsg(`Deleted "${group.name}".`); await load(); }
      else { setMsg('Delete failed.'); }
    } catch (e) { setMsg('Connection error.'); }
  };

  const addGroup = () => setGroups((gs) => [blankGroup(), ...gs]);

  const reseed = async () => {
    if (!window.confirm('Restore the default IPMI committees from the website snapshot? This overwrites the default committee groups (your custom groups are kept).')) return;
    setReseeding(true); setMsg('');
    try {
      const res = await fetch(`${API_URL}/api/kb/admin/governance/reseed`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ password }),
      });
      if (res.ok) { setMsg('Defaults restored.'); await load(); }
      else { setMsg('Reseed failed.'); }
    } catch (e) { setMsg('Connection error.'); }
    setReseeding(false);
  };

  return (
    <div className="kb-admin-section" data-testid="kb-governance-admin-section">
      <h2 className="kb-admin-section-title">IPMI Organization &amp; Governance</h2>
      <p className="kb-admin-upload-desc">
        Manage the permanent IPMI org profile — boards, committees, and staff. People auto-link to member
        profiles by name (handles nicknames &amp; middle names); use the <Link2 size={12} style={{ display: 'inline', verticalAlign: 'middle' }} /> button on a
        person to manually link them when the names differ (e.g. "CJ Sapre" → "Chiranjit Sapre").
        These power the pinned "IPMI" card in the directory and the chat's governance answers.
        Any group you edit here is marked <strong>EDITED</strong> and is preserved across app updates
        (it won't be overwritten). Use "Restore default committees" to revert to the built-in lists.
      </p>

      <div style={{ display: 'flex', gap: 8, margin: '12px 0 16px', flexWrap: 'wrap' }}>
        <button type="button" onClick={addGroup} data-testid="kb-gov-add-group"
          style={{ display: 'inline-flex', alignItems: 'center', gap: 6, padding: '8px 16px', borderRadius: 8,
            border: 'none', background: 'var(--kb-accent, #2563EB)', color: '#fff', fontWeight: 600, fontSize: 13, cursor: 'pointer' }}>
          <Plus size={14} /> Add board / committee / staff
        </button>
        <button type="button" onClick={reseed} disabled={reseeding} data-testid="kb-gov-reseed" style={ghostBtn}>
          {reseeding ? <Loader2 size={14} className="spin" /> : <RotateCcw size={14} />} Restore default committees
        </button>
      </div>

      {msg && <div data-testid="kb-gov-msg" style={{ marginBottom: 12, fontSize: 13, color: 'var(--kb-accent, #2563EB)' }}>{msg}</div>}

      {!loading && org && <OrgProfileEditor org={org} password={password} onSaved={(newOrg) => newOrg && setOrg(newOrg)}
        autoOpenSignal={adminFocus === 'org-profile'} onConsumed={() => setAdminFocus(null)} />}

      {loading ? (
        <div className="kb-members-loading"><Loader2 className="spin" size={18} /> Loading…</div>
      ) : groups.length === 0 ? (
        <p className="kb-admin-empty">No governance groups yet. Click "Add" or "Restore default committees".</p>
      ) : (
        groups.map((g, idx) => (
          <GroupEditor
            key={g.slug || `new-${idx}`}
            group={g}
            password={password}
            saving={savingSlug === (g.slug || 'new')}
            onChange={(next) => updateGroup(idx, next)}
            onSave={saveGroup}
            onDelete={deleteGroup}
          />
        ))
      )}
    </div>
  );
};

export default GovernanceAdmin;
